import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PayrollProviderList } from './PayrollProviderList';
import { Lock, ShieldCheck, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
export function PayrollConnector() {
  const [step, setStep] = useState<'select' | 'auth' | 'success'>('select');
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const handleConnect = () => {
    setIsConnecting(true);
    // Simulate OAuth flow
    setTimeout(() => {
      setIsConnecting(false);
      setStep('success');
    }, 2000);
  };
  return <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-2009">
      <div className="p-6 border-b border-gray-100 bg-gray-50" data-id="element-2010">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center" data-id="element-2011">
          <Lock className="h-5 w-5 text-blue-600 mr-2" data-id="element-2012" />
          Connect Payroll
        </h3>
        <p className="text-sm text-gray-500 mt-1" data-id="element-2013">
          Securely link your employment data for automated rent payments.
        </p>
      </div>

      <div className="p-6" data-id="element-2014">
        <AnimatePresence mode="wait" data-id="element-2015">
          {step === 'select' && <motion.div key="select" initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 20
        }} data-id="element-2016">
              <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-2017">
                Select your payroll provider
              </h4>
              <PayrollProviderList onSelect={id => {
            setSelectedProvider(id);
            setStep('auth');
          }} data-id="element-2018" />
              <div className="mt-6 flex items-center justify-center text-xs text-gray-400" data-id="element-2019">
                <ShieldCheck className="h-3 w-3 mr-1" data-id="element-2020" />
                Bank-grade security • NDPR Compliant • 256-bit Encryption
              </div>
            </motion.div>}

          {step === 'auth' && <motion.div key="auth" initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 20
        }} className="text-center py-8" data-id="element-2021">
              <div className="mx-auto h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center mb-6" data-id="element-2022">
                <Lock className="h-8 w-8 text-blue-600" data-id="element-2023" />
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2" data-id="element-2024">
                Authenticate Connection
              </h4>
              <p className="text-sm text-gray-500 mb-8 max-w-sm mx-auto" data-id="element-2025">
                You will be redirected to securely log in with your payroll
                provider. Renta does not store your login credentials.
              </p>

              <div className="flex flex-col gap-3 max-w-xs mx-auto" data-id="element-2026">
                <button onClick={handleConnect} disabled={isConnecting} className="w-full flex justify-center items-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50" data-id="element-2027">
                  {isConnecting ? <>
                      <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" data-id="element-2028" />
                      Connecting...
                    </> : 'Proceed to Login'}
                </button>
                <button onClick={() => setStep('select')} className="text-sm text-gray-500 hover:text-gray-700 font-medium" data-id="element-2029">
                  Cancel
                </button>
              </div>
            </motion.div>}

          {step === 'success' && <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="text-center py-8" data-id="element-2030">
              <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mb-6" data-id="element-2031">
                <CheckCircle className="h-8 w-8 text-green-600" data-id="element-2032" />
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2" data-id="element-2033">
                Successfully Connected!
              </h4>
              <p className="text-sm text-gray-500 mb-8" data-id="element-2034">
                Your payroll account has been linked. You can now set up
                automated rent deductions.
              </p>

              <div className="bg-blue-50 rounded-lg p-4 text-left mb-6" data-id="element-2035">
                <div className="flex items-start" data-id="element-2036">
                  <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" data-id="element-2037" />
                  <div data-id="element-2038">
                    <h5 className="text-sm font-medium text-blue-900" data-id="element-2039">
                      Next Step: Authorization
                    </h5>
                    <p className="text-xs text-blue-700 mt-1" data-id="element-2040">
                      Your employer needs to approve the rent deduction request.
                      We've sent a notification to your HR department.
                    </p>
                  </div>
                </div>
              </div>

              <button className="text-blue-600 hover:text-blue-700 font-medium text-sm" data-id="element-2041">
                View Deduction Settings
              </button>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}